<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <h5 class="mb-0">
          {{ $t('profile.sections.materials') || 'Materials' }}
        </h5>
      </div>
      <div v-if="canCreateMaterials" class="d-flex align-items-center">
        <button @click="openAddModal" class="btn btn-sm btn-primary">
          <i class="ti ti-plus me-1"></i>
          {{ $t('common.actions.add') || 'Add' }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <!-- Search and filter bar -->
      <div class="mb-3">
        <div class="input-group">
          <span class="input-group-text"><i class="ti ti-search"></i></span>
          <input
            v-model="searchText"
            type="search"
            class="form-control"
            :placeholder="$t('common.search.placeholder') || 'Search materials...'"
            @keyup.enter="searchMaterials"
          />
        </div>
      </div>

      <div v-if="isLoading" class="d-flex justify-content-center py-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') || 'Loading...' }}</span>
        </div>
      </div>
      <div v-else>
        <div v-if="materials.length === 0" class="text-center py-3 text-muted">
          {{ $t('profile.materials.empty') || 'No materials found. Click "Add" to add materials.' }}
        </div>
        <ul v-else class="list-group list-group-flush">
          <li v-for="material in materials" :key="material.id" class="list-group-item px-0">
            <div class="d-flex align-items-center">
              <div class="material-img-container me-3">
                <div v-if="material.fileUploadId" class="material-img-preview">
                  <img-preview 
                    :file-id="material.fileUploadId" 
                    width="60px" 
                    height="60px" 
                    style="object-fit: cover;" 
                    class="img-thumbnail"
                  />
                </div>
                <div v-else class="placeholder-thumbnail d-flex align-items-center justify-content-center bg-light" style="width: 60px; height: 60px; border: 1px dashed #ccc;">
                  <i class="ti ti-photo text-muted" style="font-size: 1.5rem;"></i>
                </div>
              </div>
              <div class="flex-grow-1 d-flex align-items-center justify-content-between">
                <div>
                  <h6 class="mb-1">{{ material.name }}</h6>
                  <p class="mb-1 text-muted">{{ material.code }}</p>
                  <small class="text-muted">Category: {{ material.materialCategoryName }}</small>
                </div>
                <div>
                  <button v-if="canEditMaterials" @click="openEditModal(material)" class="btn btn-sm btn-outline-secondary me-1">
                    <i class="ti ti-pencil"></i>
                  </button>
                  <button v-if="canDeleteMaterials" @click="confirmDelete(material)" class="btn btn-sm btn-outline-danger">
                    <i class="ti ti-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </li>
        </ul>

        <!-- Pagination -->
        <div v-if="totalMaterials > 10" class="d-flex justify-content-between align-items-center mt-3">
          <div class="small text-muted">
            {{ $t('common.pagination.showing') || 'Showing' }} {{ (currentPage - 1) * pageSize + 1 }} 
            {{ $t('common.pagination.to') || 'to' }} {{ Math.min(currentPage * pageSize, totalMaterials) }} 
            {{ $t('common.pagination.of') || 'of' }} {{ totalMaterials }} {{ $t('profile.materials.items') || 'materials' }}
          </div>
          <div>
            <button 
              @click="prevPage" 
              class="btn btn-sm btn-outline-secondary me-1"
              :disabled="currentPage <= 1"
            >
              <i class="ti ti-chevron-left"></i>
            </button>
            <button 
              @click="nextPage" 
              class="btn btn-sm btn-outline-secondary"
              :disabled="currentPage * pageSize >= totalMaterials"
            >
              <i class="ti ti-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Material Modal -->
    <div class="modal fade" id="materialModal" tabindex="-1" aria-labelledby="materialModalLabel" aria-hidden="true" ref="materialModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="materialModalLabel">
              {{ isEditing ? $t('profile.materials.edit') || 'Edit Material' : $t('profile.materials.add') || 'Add Material' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form-group mb-3">
              <category-cascader
                v-model="materialForm.materialCategoryId"
                :label="$t('profile.materials.category') || 'Category'"
                :placeholder="$t('profile.materials.noCategory') || '-- Select Category --'"
                :help-text="$t('profile.materials.categoryHelp') || 'Select a category for this material'"
                :disabled-category-id="isEditing ? selectedMaterial?.id : ''"
                @error="handleCascaderError"
              />
            </div>

            <div class="form-group mb-3">
              <label for="materialCode" class="form-label">{{ $t('profile.materials.code') || 'Code' }} <span class="text-danger">*</span></label>
              <input
                id="materialCode"
                v-model="materialForm.code"
                type="text"
                class="form-control"
                :placeholder="$t('profile.materials.codePlaceholder') || 'Enter material code'"
                :maxlength="codeMax"
                required
                @blur="validateCodeUniqueness"
              />
              <div v-if="codeLen / codeMax >= 0.85" class="text-muted small mt-1">{{ codeLen }} / {{ codeMax }}</div>
              <div v-if="errors.code" class="text-danger mt-1">{{ errors.code }}</div>
            </div>

            <div class="form-group mb-3">
              <label for="materialName" class="form-label">{{ $t('profile.materials.name') || 'Name' }} <span class="text-danger">*</span></label>
              <input
                id="materialName"
                v-model="materialForm.name"
                type="text"
                class="form-control"
                :placeholder="$t('profile.materials.namePlaceholder') || 'Enter material name'"
                :maxlength="nameMax"
                required
              />
              <div v-if="nameLen / nameMax >= 0.85" class="text-muted small mt-1">{{ nameLen }} / {{ nameMax }}</div>
              <div v-if="errors.name" class="text-danger mt-1">{{ errors.name }}</div>
            </div>

            <div class="form-group mb-3">
              <label for="materialDescription" class="form-label">{{ $t('profile.materials.description') || 'Description' }}</label>
              <textarea
                id="materialDescription"
                v-model="materialForm.description"
                class="form-control"
                :placeholder="$t('profile.materials.descriptionPlaceholder') || 'Enter description'"
                rows="3"
                :maxlength="descMax"
               ></textarea>
              <div v-if="descLen / descMax >= 0.85" class="text-muted small mt-1">{{ descLen }} / {{ descMax }}</div>
            </div>

            <div class="form-group mb-3">
              <label for="materialImage" class="form-label">{{ $t('profile.materials.image') || 'Material Image' }}</label>
              <div class="d-flex align-items-center">
                <div class="me-3">
                  <div v-if="imagePreview" class="position-relative material-image-preview">
                    <img :src="imagePreview" alt="Material image preview" class="img-thumbnail" style="width: 100px; height: 100px; object-fit: cover;" />
                    <button 
                      type="button" 
                      class="btn btn-sm btn-danger position-absolute top-0 end-0" 
                      style="margin: -10px -10px 0 0; border-radius: 50%;"
                      @click="removeImage"
                    >
                      <i class="ti ti-x"></i>
                    </button>
                  </div>
                  <div v-else-if="materialForm.fileUploadId && isEditing" class="placeholder-thumbnail d-flex align-items-center justify-content-center bg-light" style="width: 100px; height: 100px; border: 1px dashed #ccc;">
                    <div class="spinner-border spinner-border-sm text-primary" role="status" v-if="isLoadingImage">
                      <span class="visually-hidden">{{ $t('common.loading') || 'Loading...' }}</span>
                    </div>
                    <i v-else class="ti ti-photo text-muted" style="font-size: 2rem;"></i>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <input
                    id="materialImage"
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="d-none"
                    @change="handleFileChange"
                  />
                  <button 
                    type="button" 
                    class="btn btn-outline-primary"
                    @click="$refs.fileInput.click()"
                  >
                    <i class="ti ti-upload me-1"></i>
                    {{ !materialForm.fileUploadId ? ($t('profile.materials.uploadImage') || 'Upload Image') : ($t('profile.materials.changeImage') || 'Change Image') }}
                  </button>
                  <div v-if="errors.file" class="text-danger mt-1">{{ errors.file }}</div>
                  <div class="text-muted small mt-1">
                    {{ $t('profile.materials.imageHelp') || 'Accepted formats: JPG, PNG, GIF. Max size: 1MB.' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') || 'Cancel' }}
            </button>
            <button 
              v-if="(isEditing && canEditMaterials) || (!isEditing && canEditMaterials)"
              type="button" 
              class="btn btn-info"
              @click="handleSave(false)"
              :disabled="isSaving"
            >
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isSaving ? $t('common.saving') : $t('common.save') }}
            </button>
            <button 
              v-if="(isEditing && canEditMaterials) || (!isEditing && canCreateMaterials)"
              type="button" 
              class="btn btn-primary" 
              @click="handleSave(true)"
              :disabled="isSaving"
            >
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isSaving ? $t('common.savingAndClosing') : $t('common.saveAndClose') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" ref="deleteModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteModalLabel">{{ $t('profile.materials.confirmDelete') || 'Confirm Delete' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>{{ $t('profile.materials.confirmDeleteMsg') || 'Are you sure you want to delete this material?' }}</p>
            <p><strong>{{ selectedMaterial?.name }}</strong> ({{ selectedMaterial?.code }})</p>
            <p class="text-danger">{{ $t('profile.materials.deleteWarning') || 'This action cannot be undone.' }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') || 'Cancel' }}
            </button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="deleteMaterial"
              :disabled="isDeleting"
            >
              <span v-if="isDeleting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isDeleting ? $t('common.deleting') || 'Deleting...' : $t('common.actions.delete') || 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from 'vue'; 
import { useI18n } from 'vue-i18n';
import materialService from '@/services/materialService';
import fileManagementService from '@/services/fileManagementService';
import CategoryCascader from './CategoryCascader.vue';
import ImgPreview from './ImgPreview.vue';
import Swal from 'sweetalert2';
import { MATERIAL_MAX } from '@/constants/maxLengths';

export default {
  name: 'ProfileMaterialsCard',
  components: {
    CategoryCascader,
    ImgPreview
  },
  props: {
    tenantId: {
      type: String,
      required: true
    },
    canCreateMaterials: {
      type: Boolean,
      default: false
    },
    canEditMaterials: {
      type: Boolean,
      default: false
    },
    canDeleteMaterials: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { t } = useI18n();
    // Data
    const materials = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const isDeleting = ref(false);
    const isEditing = ref(false);
    const selectedMaterial = ref(null);
    const totalMaterials = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const searchText = ref('');
    const sorting = ref('name asc');
    const imagePreview = ref(null);
    const isLoadingImage = ref(false);
    const fileInput = ref(null);
    const selectedFile = ref(null);
    
    // Modal refs
    const materialModal = ref(null);
    const deleteModal = ref(null);
    
    // Form data
    const materialForm = ref({
      name: '',
      code: '',
      description: '',
      materialCategoryId: '',
      fileUploadId: null
    });
    const nameMax = MATERIAL_MAX.name;
    const codeMax = MATERIAL_MAX.code;
    const descMax = MATERIAL_MAX.description;
    const nameLen = computed(() => materialForm.value.name?.length || 0);
    const codeLen = computed(() => materialForm.value.code?.length || 0);
    const descLen = computed(() => materialForm.value.description?.length || 0);
    
    // Validation errors
    const errors = ref({
      name: '',
      code: '',
      file: ''
    });

    // Initialize modals and load data on component mount
    onMounted(() => {
      if (window.bootstrap) {
        // Wait for next tick to ensure DOM elements are rendered
        nextTick(() => {
          // Use querySelector instead of getElementById for more reliable element selection
          const materialModalEl = document.querySelector('#materialModal');
          const deleteModalEl = document.querySelector('#deleteModal');
          
          if (materialModalEl) {
            materialModal.value = new window.bootstrap.Modal(materialModalEl);
          } else {
            console.error('Material modal element not found');
          }
          
          if (deleteModalEl) {
            deleteModal.value = new window.bootstrap.Modal(deleteModalEl);
          } else {
            console.error('Delete modal element not found');
          }
        });
      } else {
        console.error('Bootstrap is not loaded');
      }

      // Load materials
      searchMaterials();
    });

    // Methods
    const searchMaterials = async () => {
      isLoading.value = true;
      try {
        const searchParams = {
          searchText: searchText.value,
          page: currentPage.value,
          pageSize: pageSize.value,
          sorting: sorting.value
        };
        
        const response = await materialService.searchMaterials(searchParams);
        materials.value = response.items || [];
        totalMaterials.value = response.totalCount || 0;
      } catch (error) {
        console.error('Error searching materials:', error);
        // Use Vuex store for notification
        if (window.$store) {
          window.$store.commit('SET_NOTIFICATION', { 
            message: 'Failed to load materials', 
            type: 'error' 
          });
        }
      } finally {
        isLoading.value = false;
      }
    };

    const handleCascaderError = (message) => {
      console.error('Category cascader error:', message);
      if (window.$store) {
        window.$store.commit('SET_NOTIFICATION', { 
          message: message,
          type: 'error' 
        });
      }
    };

    const openAddModal = () => {
      isEditing.value = false;
      resetForm();
      // Clear any ongoing processes
      selectedFile.value = null;
      imagePreview.value = null;
      
      // Force cascader refresh by setting a key to the current timestamp
      nextTick(() => {
        materialModal.value?.show();
      });
    };

    const openEditModal = async (material) => {
      isEditing.value = true;
      selectedMaterial.value = material;
      materialForm.value = {
        name: material.name,
        code: material.code,
        description: material.description || '',
        materialCategoryId: material.materialCategoryId || material.categoryId || '',
        fileUploadId: material.fileUploadId || null,
        id: material.id || null
      };
      
      // Clear any existing preview
      imagePreview.value = null;
      
      // If there's a file ID, get the preview
      if (material.fileUploadId) {
        await loadImagePreview(material.fileUploadId);
      }
      
      // Force cascader refresh when showing modal
      nextTick(() => {
        materialModal.value?.show();
      });
    };

    const loadImagePreview = async (fileId) => {
      if (!fileId) {
        return;
      }
      
      isLoadingImage.value = true;
      try {
        const response = await fileManagementService.getFilePreviewUrl(fileId);
        if (response && response.blobUrl) {
          imagePreview.value = response.blobUrl;
        } else {
          console.error('Invalid response format from getFilePreviewUrl:', response);
        }
      } catch (error) {
        console.error('Error loading image preview:', error);
        // No need to show an error toast for this
      } finally {
        isLoadingImage.value = false;
      }
    };

    const handleFileChange = async (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      
      
      // Validate file size (1MB limit)
      const maxSize = 1 * 1024 * 1024; // 1MB in bytes
      if (file.size > maxSize) {
        const errorMessage = t('profile.materials.fileTooLarge') || 'File is too large. Maximum size is 1MB.';
        errors.value.file = errorMessage;
        if (window.$store) {
          window.$store.commit('SET_NOTIFICATION', { 
            message: errorMessage,
            type: 'error' 
          });
        }
        // Reset the file input
        if (fileInput.value) {
          fileInput.value.value = '';
        }
        return;
      }
      
      // Validate file type (images only)
      if (!file.type.match('image.*')) {
        const errorMessage = t('profile.materials.invalidFileType') || 'Invalid file type. Only images (JPEG, PNG, GIF) are allowed.';
        errors.value.file = errorMessage;
        if (window.$store) {
          window.$store.commit('SET_NOTIFICATION', { 
            message: errorMessage,
            type: 'error' 
          });
        }
        // Reset the file input
        if (fileInput.value) {
          fileInput.value.value = '';
        }
        return;
      }
      
      errors.value.file = ''; // Clear any previous errors
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        imagePreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Store the file for later upload (when saving)
      selectedFile.value = file;
      
      // Clear any existing fileUploadId
      if (materialForm.value.fileUploadId) {
        materialForm.value.fileUploadId = null;
      }
    };
    
    const removeImage = () => {
      imagePreview.value = null;
      materialForm.value.fileUploadId = null;
      selectedFile.value = null;
      
      // Reset the file input so the same file can be selected again
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };

    const confirmDelete = (material) => {
      selectedMaterial.value = material;
      
      // Use SweetAlert2 instead of Bootstrap modal
      Swal.fire({
        title: t('profile.materials.confirmDelete') || 'Confirm Delete',
        text: t('profile.materials.confirmDeleteMsg') || 'Are you sure you want to delete this material?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('common.actions.delete') || 'Delete',
        cancelButtonText: t('common.cancel') || 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteMaterial();
        }
      });
    };

    const resetForm = () => {
      materialForm.value = {
        name: '',
        code: '',
        description: '',
        materialCategoryId: '',
        fileUploadId: null
      };
      errors.value = {
        name: '',
        code: '',
        file: ''
      };
      imagePreview.value = null;
      selectedFile.value = null;
      
      // Reset the file input
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };

    const validateForm = () => {
      let isValid = true;
      errors.value = {
        name: '',
        code: '',
        file: ''
      };
      
      if (!materialForm.value.name.trim()) {
        errors.value.name = 'Name is required';
        isValid = false;
      }
      
      if (!materialForm.value.code.trim()) {
        errors.value.code = 'Code is required';
        isValid = false;
      }
      
      return isValid;
    };

    const validateCodeUniqueness = async () => {
      if (!materialForm.value.code.trim()) {
        return;
      }

      try {
        const idToUse = isEditing.value ? selectedMaterial.value.id : props.tenantId;
        
        // Don't check if code hasn't changed in edit mode
        if (isEditing.value && materialForm.value.code === selectedMaterial.value.code) {
          errors.value.code = '';
          return;
        }

        if (!idToUse) {
          console.warn('Cannot check code uniqueness without a valid ID.');
          return;
        }

        const isUnique = await materialService.isCodeUnique(
          idToUse,
          materialForm.value.code
        );
        
        if (!isUnique) {
          errors.value.code = 'This code is already in use';
        } else {
          errors.value.code = '';
        }
      } catch (error) {
        console.error('Error checking code uniqueness:', error);
        if (window.$store) {
          window.$store.commit('SET_NOTIFICATION', { 
            message: 'Failed to verify code uniqueness', 
            type: 'error' 
          });
        }
      }
    };

    // Helper: Save material (create or update)
    const saveMaterial = async () => {
      const isUpdate = !!materialForm.value.id;
      
      if (isUpdate) {
        return await materialService.updateMaterial(materialForm.value.id, materialForm.value);
      }
      
      return await materialService.createMaterial(materialForm.value);
    };

    // Helper: Upload material file
    const uploadMaterialFile = async (materialId) => {
      if (!materialId) {
        throw new Error('Material ID is required for file upload');
      }
      
      const preparedFiles = await fileManagementService.prepareFileUploadDtos(
        [selectedFile.value], 
        materialId, 
        'MATERIAL'
      );
      
      const uploadResponse = await fileManagementService.uploadFilesJsonAsync(
        materialId, 
        preparedFiles, 
        'MATERIAL'
      );
      
      return uploadResponse;
    };

    // Helper: Update material with file ID
    const updateMaterialWithFile = async (materialId, fileUploadId) => {
      materialForm.value.fileUploadId = fileUploadId;
      return await materialService.updateMaterial(materialId, materialForm.value);
    };

    // Helper: Handle file upload process
    const handleFileUploadProcess = async (materialId) => {
      if (!selectedFile.value) {
        return null;
      }
      
      const uploadResponse = await uploadMaterialFile(materialId);
      
      if (uploadResponse?.files?.length > 0) {
        const fileUploadId = uploadResponse.files[0].id;
        return await updateMaterialWithFile(materialId, fileUploadId);
      }
      
      return null;
    };

    // Helper: Show success notification
    const showSuccessNotification = () => {
      if (window.$store) {
        window.$store.commit('SET_NOTIFICATION', { 
          message: 'Material saved successfully', 
          type: 'success' 
        });
      }
    };

    // Helper: Handle error notification
    const handleSaveError = (error) => {
      console.error('Error in handleSave:', error);
      errors.value.general = error.message || t('profile.materials.saveFailed') || 'Failed to save material';
    };

    // Helper: Switch to edit mode after creating
    const switchToEditMode = (savedMaterial) => {
      if (!savedMaterial) return;
      
      isEditing.value = true;
      selectedMaterial.value = { ...savedMaterial };
      materialForm.value.id = savedMaterial.id;
    };

    // Helper: Update material state after editing
    const updateMaterialState = (savedMaterial) => {
      if (savedMaterial) {
        selectedMaterial.value = { ...savedMaterial };
      }
    };

    // Helper: Handle post-save actions
    const handlePostSaveActions = async (savedMaterial, shouldClose) => {
      await searchMaterials();
      
      if (shouldClose) return;
      
      if (!isEditing.value) {
        switchToEditMode(savedMaterial);
      } else {
        updateMaterialState(savedMaterial);
      }
    };

    const handleSave = async (shouldClose = false) => {
      if (!validateForm()) {
        return;
      }
      
      await validateCodeUniqueness();
      if (errors.value.code) {
        return;
      }
      
      isSaving.value = true;
      let savedMaterial = null;
      errors.value = {};
      
      try {
        savedMaterial = await saveMaterial();
        materialForm.value.id = savedMaterial.id;
        
        const fileUpdatedMaterial = await handleFileUploadProcess(savedMaterial.id);
        if (fileUpdatedMaterial) {
          savedMaterial = fileUpdatedMaterial;
        }
        
        if (shouldClose && materialModal.value) {
          materialModal.value.hide();
        }
        
        showSuccessNotification();
        
        await handlePostSaveActions(savedMaterial, shouldClose);
      } catch (error) {
        handleSaveError(error);
      } finally {
        isSaving.value = false;
      }
    };

    const deleteMaterial = async () => {
      if (!selectedMaterial.value) return;
      
      isDeleting.value = true;
      
      try {
        await materialService.deleteMaterial(selectedMaterial.value.id);
        if (window.$store) {
          window.$store.commit('SET_NOTIFICATION', { 
            message: 'Material deleted successfully', 
            type: 'success' 
          });
        }
        
        // Close modal and refresh materials
        deleteModal.value?.hide();
        searchMaterials();
      } catch (error) {
        console.error('Error deleting material:', error);
        
        // Extract specific error message from API response if available
        let apiErrorMessage = 'Failed to delete material';
        if (error.response?.data?.error?.message) {
          apiErrorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          apiErrorMessage = error.response.data.message;
        }
        
        if (window.$store) {
          window.$store.commit('SET_NOTIFICATION', { 
            message: apiErrorMessage, 
            type: 'error' 
          });
        }
        
        // Close the delete confirmation modal after a short delay
        setTimeout(() => {
          if (deleteModal.value) {
            deleteModal.value.hide();
          }
        }, 100);
      } finally {
        isDeleting.value = false;
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        searchMaterials();
      }
    };

    const nextPage = () => {
      if (currentPage.value * pageSize.value < totalMaterials.value) {
        currentPage.value++;
        searchMaterials();
      }
    };

    return {
      materials,
      isLoading,
      isSaving,
      isDeleting,
      isEditing,
      selectedMaterial,
      totalMaterials,
      currentPage,
      pageSize,
      searchText,
      materialForm,
      errors,
      nameMax,
      codeMax,
      descMax,
      nameLen,
      codeLen,
      descLen,
      imagePreview,
      isLoadingImage,
      fileInput,
      selectedFile,
      searchMaterials,
      openAddModal,
      openEditModal,
      confirmDelete,
      validateCodeUniqueness,
      handleSave,
      deleteMaterial,
      prevPage,
      nextPage,
      handleCascaderError,
      handleFileChange,
      removeImage
    };
  }
};
</script>

<style scoped>
.badge {
  font-size: 90%;
}

.material-img-container {
  min-width: 60px;
  min-height: 60px;
}

.material-img-preview {
  width: 60px;
  height: 60px;
  overflow: hidden;
  border-radius: 4px;
}

.placeholder-thumbnail {
  border-radius: 4px;
}
</style> 